import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { z } from "zod";
import { feedPageQueryOptions } from "../queries/deals";
import { Feed } from "../components/feed";
import { FeedControls, type FeedControlValue } from "../components/feed-controls";

const NewSearch = z.object({
  q: z.string().optional(),
  category: z.string().optional(),
  merchant: z.string().optional(),
  page: z.coerce.number().int().min(1).optional(),
});

export const Route = createFileRoute("/new")({
  validateSearch: NewSearch,
  loaderDeps: ({ search }) => ({ search }),
  loader: ({ context, deps }) => {
    const s = deps.search;
    void context.queryClient.prefetchQuery(
      feedPageQueryOptions({
        sort: "new",
        category: s.category,
        merchant: s.merchant,
        q: s.q,
        page: s.page ?? 1,
      }),
    );
  },
  component: NewPage,
});

function NewPage() {
  const search = Route.useSearch();
  const navigate = useNavigate();
  const page = search.page ?? 1;

  function apply(next: FeedControlValue) {
    const filters = {
      q: next.q,
      category: next.category,
      merchant: next.merchant,
      page: undefined,
    };
    if (next.sort !== "new") {
      navigate({ to: "/", search: filters });
      return;
    }
    navigate({ to: "/new", search: filters });
  }

  return (
    <main className="mx-auto w-full max-w-3xl px-4 sm:px-5 py-6 sm:py-8 flex-1">
      <header className="flex items-baseline justify-between mb-4">
        <h1 className="text-xl font-semibold tracking-tight">New deals</h1>
        <nav className="text-sm flex items-center gap-3 text-[color:var(--color-fg-muted)]">
          <Link to="/" className="hover:text-[color:var(--color-fg)]">
            Hot
          </Link>
        </nav>
      </header>
      <FeedControls
        value={{ q: search.q, category: search.category, merchant: search.merchant, sort: "new" }}
        onApply={apply}
      />
      <Feed
        sort="new"
        category={search.category}
        merchant={search.merchant}
        q={search.q}
        page={page}
        onPage={(p) => navigate({ to: "/new", search: { ...search, page: p <= 1 ? undefined : p } })}
      />
    </main>
  );
}
